import React, { useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography, List, ListItem, ListItemButton, ListItemText, Breadcrumbs, Link as MuiLink } from '@mui/material';
import { Outlet, NavLink, Link, useParams } from 'react-router-dom';
import { LabAnswersProvider, useLabAnswers } from '../contexts/LabAnswersContext';
import { orderedSections } from '../utils/labNavigation';

function DraftControls() {
  const { clearAll, storageError, saveNotice } = useLabAnswers();
  const [open, setOpen] = useState(false);
  return <Box className="screen-only" sx={{ my: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ flex: '1 1 30rem' }}>
      <Typography variant="body2">Answers save automatically in this browser. They are not submitted to your instructor or Canvas.</Typography>
      <Typography variant="body2"><MuiLink component={Link} to="/help">Saving on shared computers and accessibility help</MuiLink></Typography>
    </Box>
    {storageError && <Alert severity="warning" sx={{ mt: 1 }}>{storageError}</Alert>}
    <Box role="status" aria-live="polite">{saveNotice}</Box>
    <Button type="button" variant="text" color="error" onClick={() => setOpen(true)}>Clear this lab’s saved answers</Button>
    <Dialog open={open} onClose={() => setOpen(false)} aria-labelledby="clear-drafts-title" aria-describedby="clear-drafts-description" maxWidth="xs" fullWidth>
      <DialogTitle id="clear-drafts-title" component="h2">Clear this lab’s saved answers?</DialogTitle>
      <DialogContent><DialogContentText id="clear-drafts-description">This removes this lab’s answers from this browser, including other open tabs. Other labs are unchanged. This cannot be undone.</DialogContentText></DialogContent>
      <DialogActions>
        <Button autoFocus onClick={() => setOpen(false)}>Cancel</Button>
        <Button color="error" onClick={() => { clearAll(); setOpen(false); }}>Clear answers</Button>
      </DialogActions>
    </Dialog>
  </Box>;
}

export function SectionNav({ content, labId }) {
  return <Box component="nav" aria-label="Lab sections" sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 3, md: 4 } }}>
    <List disablePadding sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {orderedSections(content).map(([section, sectionContent]) => <ListItem key={section} disablePadding sx={{ width: 'auto' }}>
        <ListItemButton component={NavLink} end to={`/labs/${labId}/${sectionContent.link || section}`}
          sx={{ minHeight: 48, px: { xs: 1.5, sm: 2 }, borderBottom: 3, borderColor: 'transparent',
            '&.active': { bgcolor: 'primary.light', borderColor: 'primary.main', color: 'primary.dark' } }}>
          <ListItemText primary={sectionContent.name} primaryTypographyProps={{ fontWeight: 700 }} />
        </ListItemButton>
      </ListItem>)}
    </List>
  </Box>;
}

export default function ContentFrame({ data, labName, description }) {
  const { labId } = useParams();
  return <LabAnswersProvider key={labId} labId={labId}>
    <Box sx={{ bgcolor: 'background.paper', px: { xs: 2, sm: 3, md: 5 }, pb: { xs: 4, md: 6 }, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Breadcrumbs aria-label="Breadcrumb" sx={{ pt: 2, mb: 2 }}>
        <MuiLink component={Link} to="/">Home</MuiLink>
        <MuiLink component={Link} to="/labs">Labs</MuiLink>
        <Typography aria-current="page">{labName}</Typography>
      </Breadcrumbs>
      <Typography variant="h1" component="h1">{labName}</Typography>
      <Typography sx={{ my: 2, maxWidth: '65ch' }}>{description}</Typography>
      <DraftControls />
      <SectionNav content={data.content} labId={labId} />
      <Box sx={{ minWidth: 0 }}><Outlet /></Box>
    </Box>
  </LabAnswersProvider>;
}
